import { useEffect, useState } from 'react';
import { getBender } from '@509-bender/bender-library';
import * as storage from './storage.js';

const KEY = 'appSettings';
export const LAST_BENDER_STORAGE_KEY = 'lastBender';

export const ANGLE_INCREMENT_OPTIONS = [0.5, 1, 2.5, 5] as const;
export type AngleIncrement = (typeof ANGLE_INCREMENT_OPTIONS)[number];

export const LEVEL_TOLERANCE_OPTIONS = [0.3, 0.5, 1] as const;
export type LevelTolerance = (typeof LEVEL_TOLERANCE_OPTIONS)[number];

export const LENGTH_UNIT_OPTIONS = ['Inches', 'Feet + inches', 'Centimeters'] as const;
export type LengthUnit = (typeof LENGTH_UNIT_OPTIONS)[number];

export const FRACTION_PRECISION_OPTIONS = ['1/16"', '1/8"', '1/4"'] as const;
export type FractionPrecision = (typeof FRACTION_PRECISION_OPTIONS)[number];

export const ANGLE_UNIT_OPTIONS = ['Degrees'] as const;
export type AngleUnit = (typeof ANGLE_UNIT_OPTIONS)[number];

export const BENDER_OPTIONS = [
  'Greenlee Site-Rite',
  'Klein 51604',
  'Gardner Hand Bender',
  'Greenlee 555',
] as const;
export type DefaultBender = (typeof BENDER_OPTIONS)[number];

export const CONDUIT_OPTIONS = [
  '1/2"',
  '3/4"',
  '1"',
  '1-1/4"',
  '1-1/2"',
  '2"',
  '2-1/2"',
  '3"',
  '3-1/2"',
  '4"',
] as const;
export type DefaultConduit = (typeof CONDUIT_OPTIONS)[number];

export const MATERIAL_OPTIONS = ['EMT', 'Rigid', 'IMC', 'Aluminum'] as const;
export type DefaultMaterial = (typeof MATERIAL_OPTIONS)[number];

export interface BenderSelectionSettings {
  benderId: string;
  conduitSize: string;
  material: 'EMT' | 'IMC' | 'RIGID' | 'ALUMINUM';
}

export interface AppSettings {
  defaultBender: DefaultBender;
  defaultConduit: DefaultConduit;
  defaultMaterial: DefaultMaterial;
  lengthUnit: LengthUnit;
  fractionPrecision: FractionPrecision;
  angleUnit: AngleUnit;
  angleIncrement: AngleIncrement;
  levelTolerance: LevelTolerance;
  roundToNearest: FractionPrecision;
  darkMode: boolean;
  highContrastText: boolean;
  hapticFeedback: boolean;
}

const DEFAULT_SETTINGS: AppSettings = {
  defaultBender: 'Greenlee Site-Rite',
  defaultConduit: '3/4"',
  defaultMaterial: 'EMT',
  lengthUnit: 'Inches',
  fractionPrecision: '1/16"',
  angleUnit: 'Degrees',
  angleIncrement: 1,
  levelTolerance: 0.3,
  roundToNearest: '1/16"',
  darkMode: true,
  highContrastText: true,
  hapticFeedback: true,
};

const listeners = new Set<() => void>();

function isAngleIncrement(value: unknown): value is AngleIncrement {
  return typeof value === 'number' && ANGLE_INCREMENT_OPTIONS.some((option) => option === value);
}

function isLevelTolerance(value: unknown): value is LevelTolerance {
  return typeof value === 'number' && LEVEL_TOLERANCE_OPTIONS.some((option) => option === value);
}

function isOption<T extends readonly unknown[]>(options: T, value: unknown): value is T[number] {
  return options.some((option) => option === value);
}

function normalizeSettings(value: Partial<AppSettings> | undefined): AppSettings {
  const parsedConduit = parseConduitOption(value?.defaultConduit);
  return normalizeBenderDefaults({
    defaultBender: parseDefaultBender(value?.defaultBender) ?? DEFAULT_SETTINGS.defaultBender,
    defaultConduit: parsedConduit ?? DEFAULT_SETTINGS.defaultConduit,
    defaultMaterial: isOption(MATERIAL_OPTIONS, value?.defaultMaterial)
      ? value.defaultMaterial
      : DEFAULT_SETTINGS.defaultMaterial,
    lengthUnit: isOption(LENGTH_UNIT_OPTIONS, value?.lengthUnit)
      ? value.lengthUnit
      : DEFAULT_SETTINGS.lengthUnit,
    fractionPrecision: isOption(FRACTION_PRECISION_OPTIONS, value?.fractionPrecision)
      ? value.fractionPrecision
      : DEFAULT_SETTINGS.fractionPrecision,
    angleUnit: isOption(ANGLE_UNIT_OPTIONS, value?.angleUnit)
      ? value.angleUnit
      : DEFAULT_SETTINGS.angleUnit,
    angleIncrement: isAngleIncrement(value?.angleIncrement)
      ? value.angleIncrement
      : DEFAULT_SETTINGS.angleIncrement,
    levelTolerance: isLevelTolerance(value?.levelTolerance)
      ? value.levelTolerance
      : DEFAULT_SETTINGS.levelTolerance,
    roundToNearest: isOption(FRACTION_PRECISION_OPTIONS, value?.roundToNearest)
      ? value.roundToNearest
      : DEFAULT_SETTINGS.roundToNearest,
    darkMode: typeof value?.darkMode === 'boolean' ? value.darkMode : DEFAULT_SETTINGS.darkMode,
    highContrastText:
      typeof value?.highContrastText === 'boolean'
        ? value.highContrastText
        : DEFAULT_SETTINGS.highContrastText,
    hapticFeedback:
      typeof value?.hapticFeedback === 'boolean'
        ? value.hapticFeedback
        : DEFAULT_SETTINGS.hapticFeedback,
  });
}

function load(): AppSettings {
  return normalizeSettings(storage.get<Partial<AppSettings>>(KEY));
}

function save(next: AppSettings): void {
  storage.set(KEY, next);
  listeners.forEach((fn) => fn());
}

export function getAppSettings(): AppSettings {
  return load();
}

export function setAngleIncrement(angleIncrement: AngleIncrement): void {
  save({ ...load(), angleIncrement });
}

export function setAppSetting<K extends keyof AppSettings>(key: K, value: AppSettings[K]): void {
  const dirtyNext = { ...load(), [key]: value };
  const next = isBenderDefaultKey(key) ? normalizeBenderDefaults(dirtyNext) : dirtyNext;
  save(next);
  if (isBenderDefaultKey(key)) {
    syncBenderSelectionFromSettings(next);
  }
}

export function getDefaultBenderSelection(): BenderSelectionSettings {
  return settingsToBenderSelection(load());
}

export function formatDefaultConduitLabel(
  defaultConduit: DefaultConduit,
  material: DefaultMaterial = DEFAULT_SETTINGS.defaultMaterial,
): string {
  return `${defaultConduit.replace('"', '')} ${material}`;
}

export function fractionPrecisionToInches(precision: FractionPrecision): number {
  switch (precision) {
    case '1/16"':
      return 1 / 16;
    case '1/8"':
      return 1 / 8;
    case '1/4"':
      return 1 / 4;
    default:
      return 1 / 16;
  }
}

export function getMaterialOptionsForBender(defaultBender: DefaultBender): DefaultMaterial[] {
  const model = getBender(benderIdFor(defaultBender));
  const materials = new Set(model?.shoes.map((shoe) => defaultMaterialFor(shoe.material)) ?? []);
  const options = MATERIAL_OPTIONS.filter((option) => materials.has(option));
  return options.length > 0 ? options : [...MATERIAL_OPTIONS];
}

export function getConduitOptionsForDefaults(
  defaultBender: DefaultBender,
  defaultMaterial: DefaultMaterial,
): DefaultConduit[] {
  const model = getBender(benderIdFor(defaultBender));
  const material = materialFor(defaultMaterial);
  const available = new Set(
    model?.shoes
      .filter((shoe) => shoe.material === material)
      .map((shoe) => conduitOptionForSize(shoe.conduitSize)) ?? [],
  );
  const options = CONDUIT_OPTIONS.filter((option) => available.has(option));
  return options.length > 0 ? options : [...CONDUIT_OPTIONS];
}

export function syncBenderSelectionFromSettings(settings: AppSettings = load()): void {
  storage.set(LAST_BENDER_STORAGE_KEY, settingsToBenderSelection(settings));
}

export function settingsToBenderSelection(
  settings: Pick<AppSettings, 'defaultBender' | 'defaultConduit' | 'defaultMaterial'>,
): BenderSelectionSettings {
  const benderId = benderIdFor(settings.defaultBender);
  const requestedMaterial = materialFor(settings.defaultMaterial);
  const requestedSize = conduitSizeFor(settings.defaultConduit);
  const model = getBender(benderId);
  const requestedShoe = model?.shoes.find(
    (shoe) => shoe.conduitSize === requestedSize && shoe.material === requestedMaterial,
  );
  const materialFallback = model?.shoes.find((shoe) => shoe.material === requestedMaterial);
  const fallback = requestedShoe ?? materialFallback ?? model?.shoes[0];

  return {
    benderId,
    conduitSize: fallback?.conduitSize ?? requestedSize,
    material: fallback?.material ?? requestedMaterial,
  };
}

function benderIdFor(option: DefaultBender): string {
  switch (option) {
    case 'Greenlee Site-Rite':
      return 'greenlee-hand-bender';
    case 'Klein 51604':
      return 'klein-hand-bender';
    case 'Gardner Hand Bender':
      return 'gardner-bender-hand';
    case 'Greenlee 555':
      return 'greenlee-555';
    default:
      return 'greenlee-hand-bender';
  }
}

function parseDefaultBender(value: unknown): DefaultBender | undefined {
  if (isOption(BENDER_OPTIONS, value)) return value;
  if (value === 'Greenlee 1818' || value === 'Greenlee Hand Bender') {
    return 'Greenlee Site-Rite';
  }
  if (value === 'Ideal 74-025') {
    return 'Gardner Hand Bender';
  }
  if (value === 'Ridgid B-600') {
    return 'Greenlee 555';
  }
  return undefined;
}

function conduitSizeFor(option: DefaultConduit): string {
  return option.replace('"', '');
}

function materialFor(option: DefaultMaterial): BenderSelectionSettings['material'] {
  if (option === 'Rigid') return 'RIGID';
  if (option === 'Aluminum') return 'ALUMINUM';
  return option;
}

function defaultMaterialFor(material: BenderSelectionSettings['material']): DefaultMaterial {
  if (material === 'RIGID') return 'Rigid';
  if (material === 'ALUMINUM') return 'Aluminum';
  return material;
}

function conduitOptionForSize(conduitSize: string): DefaultConduit | undefined {
  const option = `${conduitSize}"`;
  return isOption(CONDUIT_OPTIONS, option) ? option : undefined;
}

function parseConduitOption(value: unknown): DefaultConduit | undefined {
  if (isOption(CONDUIT_OPTIONS, value)) return value;
  if (typeof value !== 'string') return undefined;
  const legacySize = value.split('"')[0];
  return conduitOptionForSize(legacySize);
}

function normalizeBenderDefaults(settings: AppSettings): AppSettings {
  const materialOptions = getMaterialOptionsForBender(settings.defaultBender);
  const defaultMaterial = materialOptions.includes(settings.defaultMaterial)
    ? settings.defaultMaterial
    : (materialOptions[0] ?? DEFAULT_SETTINGS.defaultMaterial);
  const conduitOptions = getConduitOptionsForDefaults(settings.defaultBender, defaultMaterial);
  const defaultConduit = conduitOptions.includes(settings.defaultConduit)
    ? settings.defaultConduit
    : (conduitOptions[0] ?? DEFAULT_SETTINGS.defaultConduit);

  return { ...settings, defaultConduit, defaultMaterial };
}

function isBenderDefaultKey(
  key: keyof AppSettings,
): key is 'defaultBender' | 'defaultConduit' | 'defaultMaterial' {
  return key === 'defaultBender' || key === 'defaultConduit' || key === 'defaultMaterial';
}

export function useAppSettings(): AppSettings {
  const [settings, setSettings] = useState<AppSettings>(() => load());

  useEffect(() => {
    const listener = () => setSettings(load());
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  return settings;
}
